/** DrawerComponent: reusable right-side drawer, opened by any data-drawer-trigger button and closed by backdrop, data-drawer-close or Escape; several can coexist independently. */
class DrawerComponent extends HTMLElement {
  connectedCallback() {
    // With [data-drawer-portal], the drawer is moved to <body> before anything is wired: a positioned ancestor (the sticky filter bar) would otherwise trap its fixed panel in its own stacking context, capping it at the ancestor's z-index instead of its own 100/101. Moving re-enters connectedCallback, hence the early return.
    if (this.hasAttribute("data-drawer-portal") && this.parentElement !== document.body) {
      // A section re-render (theme editor, Section Rendering API) ships a fresh copy while the moved one still sits in <body>: the newcomer evicts its stale twin rather than duplicating the id.
      if (this.id) {
        document.querySelectorAll(`drawer-component#${CSS.escape(this.id)}`).forEach((el) => {
          if (el !== this) el.remove();
        });
      }
      document.body.appendChild(this);
      return;
    }

    this._backdrop = this.querySelector("[data-drawer-backdrop]");
    this._panel = this.querySelector("[data-drawer-panel]");

    // Fermé au départ : le panneau est retiré de l'ordre de tabulation et des
    // lecteurs d'écran tant qu'il n'est pas ouvert (`inert`).
    if (!this.isOpen && this._panel) this._panel.inert = true;

    this._backdrop?.addEventListener("click", () => this.close());

    this.querySelectorAll("[data-drawer-close]").forEach((btn) => {
      btn.addEventListener("click", () => this.close());
    });

    this._onKeydown = (e) => {
      if (e.key === "Escape") {
        this.close();
        return;
      }
      if (e.key === "Tab") this._trapTab(e);
    };

    this._onDocClick = (e) => {
      const trigger = e.target.closest("[data-drawer-trigger]");
      if (trigger && trigger.dataset.drawerTrigger === this.id) {
        this._triggerEl = trigger;
        this.open();
      }
    };

    document.addEventListener("click", this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this._onKeydown);
  }

  /** Focusable elements currently reachable in the panel; `visibility` is inherited, so testing the element alone also rules out a collapsed accordion. */
  _focusables() {
    if (!this._panel) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return [...this._panel.querySelectorAll(selector)].filter((el) => {
      const style = getComputedStyle(el);
      return style.visibility !== "hidden" && style.display !== "none";
    });
  }

  /** The panel declares aria-modal, so Tab must cycle inside it instead of walking the page left behind the backdrop. */
  _trapTab(event) {
    const items = this._focusables();
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !this._panel.contains(active);

    if (event.shiftKey ? active === first || outside : active === last || outside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }

  open() {
    this.setAttribute("data-open", "");
    // Lock on <html> only, like cart-drawer.js: overflow:hidden on <body> turns it into a scroll container, which unpins every position:sticky it holds — the header first.
    document.documentElement.style.overflow = "hidden";
    if (this._panel) this._panel.inert = false;
    document.addEventListener("keydown", this._onKeydown);

    // Focus sur le premier élément focusable dans le panel.
    requestAnimationFrame(() => {
      this._focusables()[0]?.focus();
    });

    this.dispatchEvent(new CustomEvent("drawer:open", { bubbles: true }));
  }

  close() {
    this.removeAttribute("data-open");
    document.documentElement.style.overflow = "";
    if (this._panel) this._panel.inert = true;
    document.removeEventListener("keydown", this._onKeydown);

    // Rend le focus au bouton déclencheur.
    this._triggerEl?.focus();
    this._triggerEl = null;

    this.dispatchEvent(new CustomEvent("drawer:close", { bubbles: true }));
  }

  get isOpen() {
    return this.hasAttribute("data-open");
  }
}

customElements.define("drawer-component", DrawerComponent);
